import React, { useState } from 'react';
import { Bell, BellOff, Mail, Clock, X, CheckCircle } from 'lucide-react';

/**
 * Tracking Confirmation Modal
 * Shows popup to confirm automated tracking setup with email notifications
 */
const TrackingConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  brandName,
  affiliateUrl,
  currentPosition
}) => {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleStart = async () => {
    setIsProcessing(true);
    try {
      await onConfirm(emailNotifications);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setEmailNotifications(true); // Reset to default
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full my-8 overflow-hidden transform transition-all max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 relative flex-shrink-0">
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 rounded-full p-2">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Start Automated Tracking?</h2>
              <p className="text-blue-100 text-sm">24-hour position monitoring</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {/* Info Section */}
          <div className="mb-6">
            <p className="text-gray-700 dark:text-slate-300 mb-4">
              Position will be tracked automatically every <strong>24 hours</strong> and you'll be notified via email if it changes.
            </p>

            {/* Brand Details */}
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Brand</label>
                <p className="text-gray-800 dark:text-slate-200 font-medium">{brandName}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Affiliate URL</label>
                <p className="text-gray-600 dark:text-slate-400 text-sm break-all">{affiliateUrl}</p>
              </div>

              {currentPosition && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Current Position</label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">#{currentPosition}</span>
                    </div>
                    <span className="text-gray-600 dark:text-slate-400 text-sm">Position {currentPosition}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="mb-6 space-y-3">
            <h3 className="font-semibold text-gray-800 dark:text-slate-200 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              What you'll get:
            </h3>
            <ul className="space-y-2 ml-7">
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-slate-400">
                <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Automatic position checks every 24 hours</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-slate-400">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Email notifications when position changes</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-600 dark:text-slate-400">
                <BellOff className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <span>Can be stopped or disabled anytime</span>
              </li>
            </ul>
          </div>

          {/* Email Notifications Toggle */}
          <div className="mb-6">
            <label className="flex items-center justify-between cursor-pointer p-4 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-6 rounded-full transition-colors ${emailNotifications ? 'bg-blue-600' : 'bg-gray-300 dark:bg-slate-700'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform mt-0.5 ${emailNotifications ? 'translate-x-6 ml-1' : 'translate-x-0.5'}`}></div>
                </div>
                <div>
                  <span className="font-medium text-gray-800 dark:text-slate-200">Email Notifications</span>
                  <p className="text-xs text-gray-600 dark:text-slate-400">Get notified when position changes</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={emailNotifications}
                onChange={(e) => setEmailNotifications(e.target.checked)}
                className="sr-only"
                disabled={isProcessing}
              />
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={isProcessing}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleStart}
              disabled={isProcessing}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Starting...</span>
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  <span>Start Tracking</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingConfirmModal;
